import { useCallback, useMemo, useState } from 'react';
import { matchPath, useLocation } from 'react-router';

import { useSiteTourStep } from 'my-core/hooks';
import { useOnlineCourseContext } from 'my-core/in-course-experience-hooks';
import { getComponentAtPath } from 'my-core/online-course-content-map';
import {
  studentCoursePageAssessmentRelativePath,
  studentCoursePageFlashCardsRelativePath,
  studentCoursePageGradeBoostersRelativePath,
  studentCoursePageMockExamRelativePath,
  studentCoursePageOnDemandRelativePath,
  studentCoursePagePath,
} from 'my-core/routes';
import { isStudySessionListable, STUDY_SESSION_TYPES } from 'my-core/study-session-utils';

import OnlineCourseLayout, {
  DRAWER_MODE_BREAKPOINT,
  DRAWER_TRANSITION_DURATION,
} from 'my-components/OnlineCourseLayout';
import ErrorMessage from 'my-elements/ErrorMessage';

import { TOUR_ID, WELCOME_STEP } from './student-course-page-tour';
import StudentCoursePageAssessment from './StudentCoursePageAssessment';
import StudentCoursePageFlashCards from './StudentCoursePageFlashCards';
import StudentCoursePageGradeBoostersPage from './StudentCoursePageGradeBoostersPage';
import StudentCoursePageMockExam from './StudentCoursePageMockExam';
import StudentCoursePageNotFound from './StudentCoursePageNotFound';
import StudentCoursePageOnDemand from './StudentCoursePageOnDemand';
import StudentCoursePageOverview from './StudentCoursePageOverview';
import StudentCoursePageSidebar from './StudentCoursePageSidebar';
import StudentCoursePageStudySession from './StudentCoursePageStudySession';

import useMediaQuery from '@mui/material/useMediaQuery';

export default function StudentCoursePage({
  assessments,
  classPass,
  classPassContentMap,
  classRepCourse,
  course,
  courseInstructor,
  courseSubscription,
  currentUser,
  fetchStatus,
  flashCardDecks,
  idOrSlug,
  onlineCourses,
  practiceModeSectionResults,
  studySessionBundles,
  studySessions: studySessionsProp,
  subscription,
  userCourse,
}) {
  const location = useLocation();
  const shouldCloseDrawerOnNav = useMediaQuery(({ breakpoints }) => breakpoints.down(DRAWER_MODE_BREAKPOINT));

  const isSmall = useMediaQuery(({ breakpoints }) => breakpoints.down('lg'));
  const [drawerOpen, setDrawerOpen] = useState(!isSmall);
  const toggleDrawerOpen = useCallback(() => setDrawerOpen(open => !open), [setDrawerOpen]);

  const fetching = !fetchStatus || fetchStatus.fetching;

  const [pathMatchData, activeRouteConfig] = useMemo(() => {
    const pathname = location.pathname.replace(studentCoursePagePath(idOrSlug), '') || '/';
    let activeMatch, activeRoute;
    for (const route of ROUTES) {
      const match = matchPath(route.path || '', pathname);
      if (match) {
        activeMatch = match;
        activeRoute = route;
        break;
      }
    }
    // return [activeMatch, activeRoute.Component];
    if (!activeMatch) return [{}, NOT_FOUND_ROUTE];
    return [{ pathname: activeMatch.pathnameBase.slice(1), params: activeMatch.params }, activeRoute];
  }, [idOrSlug, location.pathname]);
  const Component = activeRouteConfig.Component;

  const listableStudySessions = useMemo(() => studySessionsProp.filter(isStudySessionListable), [studySessionsProp]);
  const studySessions = activeRouteConfig.studySessionDetail ? studySessionsProp : listableStudySessions;

  const routeData = useMemo(() => {
    const result = {};
    function getActiveCompData(_, contentMap) {
      const result = {};
      const pathPartsParam = pathMatchData.params['*'];
      if (pathPartsParam && contentMap) {
        const pathParts = pathPartsParam.split('/');
        if (pathParts?.[0] === 'practice-mode') {
          result.practiceMode = true;
          pathParts.shift();
        }
        const component = getComponentAtPath(pathParts, contentMap);
        // the remaining node types group the tree for the sidebar and have no page of their own
        if (['activity', 'chapter', 'section'].includes(component?.type)) result.activeComponent = component;
      }
      return result;
    }
    switch (activeRouteConfig.key) {
      case 'assessment': {
        const assessment = assessments.find(a => a.id === +pathMatchData.params.id);
        if (assessment) {
          result.assessment = assessment;
          Object.assign(result, getActiveCompData(pathMatchData.params['*'], assessment.contents));
        }
        break;
      }
      case 'on-demand':
        Object.assign(result, getActiveCompData(pathMatchData.params['*'], classPassContentMap));
        break;
    }
    return result;
  }, [pathMatchData, assessments, activeRouteConfig, classPassContentMap]);

  // console.log('PATH MATCH DATA', pathMatchData);

  const { canViewAll, onlineCourseUser } = useOnlineCourseContext(currentUser, classPass, course);

  const welcomeTourStep = useMemo(
    () => ({
      ...WELCOME_STEP,
      onAfter: cb => {
        setDrawerOpen(open => {
          if (open) {
            cb();
          } else {
            setTimeout(cb, DRAWER_TRANSITION_DURATION + 100);
          }
          return true;
        });
      },
    }),
    [],
  );

  const showSidebar = !activeRouteConfig.hideSidebar && (fetching || classPass);

  useSiteTourStep({ tourId: TOUR_ID, step: welcomeTourStep, disabled: !showSidebar });

  if (fetchStatus?.error) return <ErrorMessage error={fetchStatus.error} fullPage />;

  return (
    <OnlineCourseLayout
      disableAutoCapture
      drawer={
        showSidebar && (
          <StudentCoursePageSidebar
            activeComponent={routeData.activeComponent}
            assessments={assessments}
            canViewAll={canViewAll}
            classPass={classPass}
            classPassContentMap={classPassContentMap}
            course={course}
            currentUser={currentUser}
            drawerOpen={drawerOpen}
            fetching={fetching}
            // flashCardDecks={flashCardDecks}
            onClose={toggleDrawerOpen}
            // onlineCourses={onlineCourses}
            onlineCourseUser={onlineCourseUser}
            pathMatchData={pathMatchData}
            practiceMode={routeData.practiceMode}
            practiceModeSectionResults={practiceModeSectionResults}
            shouldCloseDrawerOnNav={shouldCloseDrawerOnNav}
            studySessions={listableStudySessions}
          />
        )
      }
      drawerOpen={drawerOpen}
      onClose={toggleDrawerOpen}
    >
      <Component
        {...routeData}
        assessments={assessments}
        classPass={classPass}
        classPassContentMap={classPassContentMap}
        classRepCourse={classRepCourse}
        course={course}
        courseInstructor={courseInstructor}
        courseSubscription={courseSubscription}
        currentUser={currentUser}
        drawerOpen={drawerOpen}
        fetching={fetching}
        fetchStatus={fetchStatus}
        flashCardDecks={flashCardDecks}
        onlineCourses={onlineCourses}
        onlineCourseUser={onlineCourseUser}
        onToggleDrawer={showSidebar && toggleDrawerOpen}
        pathMatchData={pathMatchData}
        studySessionBundles={studySessionBundles}
        studySessions={studySessions}
        subscription={subscription}
        userCourse={userCourse}
      />
    </OnlineCourseLayout>
  );
}

const ROUTES = [
  { key: 'overview', path: '', Component: StudentCoursePageOverview },
  {
    key: 'boosters',
    path: studentCoursePageGradeBoostersRelativePath(),
    Component: StudentCoursePageGradeBoostersPage,
  },
  {
    key: 'assessment',
    path: `${studentCoursePageAssessmentRelativePath(':id')}/*`,
    Component: StudentCoursePageAssessment,
  },
  {
    key: 'on-demand',
    path: `${studentCoursePageOnDemandRelativePath()}/*`,
    Component: StudentCoursePageOnDemand,
  },
  {
    key: 'mocks',
    path: studentCoursePageMockExamRelativePath(':id'),
    Component: StudentCoursePageMockExam,
  },
  {
    key: 'flash-cards',
    path: studentCoursePageFlashCardsRelativePath(':id'),
    Component: StudentCoursePageFlashCards,
  },
  ...Object.entries(STUDY_SESSION_TYPES).map(([key, config]) => ({
    key: `ss_${key}`,
    path: `${config.slug}/:id`,
    Component: StudentCoursePageStudySession,
    hideSidebar: true,
    studySessionDetail: true,
  })),
];
const NOT_FOUND_ROUTE = { key: 'not-found', Component: StudentCoursePageNotFound };
